import React from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * Pre-finish RIR confirmation modal.
 *
 * Two scenarios surface here when the user clicks "Finish session":
 *
 *  1. failureSets — sets with rir === 0 (or rir_left === 0 on unilateral).
 *     The user marked these as trained to failure. We surface them so they
 *     can't accidentally turn a normal set into a failure-tagged set by
 *     forgetting to bump the default. Confirming locks in the intent.
 *
 *  2. lowIntensitySets — sets with rir >= 3 (i.e. "I had 3+ reps left in the
 *     tank"). Outside the 1–2 RIR target band, which signals the set may not
 *     have been hard enough to actually drive growth. The user can either
 *     fix it or confirm "yes, that's what I did".
 *
 * Either bucket alone fires the modal. If both are present they render
 * stacked in the same dialog. Both are independent confirmations the user
 * must explicitly acknowledge before the workout is logged.
 */
export default function RIRConfirmModal({ open, failureSets, lowIntensitySets, onConfirm, onCancel }) {
    if (!open) return null;

    const hasFailure = failureSets.length > 0;
    const hasLow = lowIntensitySets.length > 0;

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
            data-testid="rir-confirm-modal"
            onClick={(e) => {
                if (e.target === e.currentTarget) onCancel?.();
            }}
        >
            <div className="relative w-full max-w-md border-2 border-accent-red bg-[#0a0a0a] text-white shadow-2xl">
                <button
                    onClick={onCancel}
                    aria-label="Close"
                    data-testid="rir-confirm-close"
                    className="absolute right-2 top-2 rounded-full p-1 text-white/55 hover:text-white"
                >
                    <X className="h-4 w-4" />
                </button>
                <div className="p-6">
                    <div className="flex items-start gap-3">
                        <div className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-md bg-accent-red/15 text-accent-red">
                            <AlertTriangle className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                            <div className="label-eyebrow text-accent-red">RIR check</div>
                            <h3 className="mt-1 font-display text-xl font-black uppercase leading-tight text-white">
                                Confirm your effort before you log this session.
                            </h3>
                            <p className="mt-2 text-[13px] leading-relaxed text-white/80">
                                RIR is how Hypergains scores intensity. A couple of these sets look unusual — quickly confirm or go back and fix them.
                            </p>
                        </div>
                    </div>

                    {hasFailure && (
                        <div className="mt-5 border border-accent-red/50 bg-accent-red/10 p-4" data-testid="rir-failure-block">
                            <div className="label-eyebrow text-accent-red">RIR 0 · trained to failure</div>
                            <p className="mt-2 text-[13px] leading-relaxed text-white/90">
                                You marked <span className="font-bold text-white">{failureSets.length} set{failureSets.length === 1 ? "" : "s"}</span> with RIR 0 — meaning you went to <span className="italic">absolute</span> failure with no reps left.
                            </p>
                            <ul className="mt-2 space-y-1 text-[12px] text-white/75">
                                {failureSets.slice(0, 6).map((s, i) => (
                                    <li key={i} className="flex items-center gap-2" data-testid={`rir-failure-item-${i}`}>
                                        <span className="h-1 w-1 flex-none rounded-full bg-accent-red" />
                                        <span className="truncate">{s.exercise} · Set {s.setNumber}{s.side ? ` (${s.side})` : ""}</span>
                                    </li>
                                ))}
                                {failureSets.length > 6 && (
                                    <li className="text-white/55">+ {failureSets.length - 6} more</li>
                                )}
                            </ul>
                            <p className="mt-3 text-[11px] uppercase tracking-wider text-white/55">
                                If you forgot to update the field, hit cancel and fix it. Otherwise tap confirm.
                            </p>
                        </div>
                    )}

                    {hasLow && (
                        <div className="mt-4 border border-amber-400/40 bg-amber-400/10 p-4" data-testid="rir-low-block">
                            <div className="label-eyebrow text-amber-400">RIR 3+ · possibly under-trained</div>
                            <p className="mt-2 text-[13px] leading-relaxed text-white/90">
                                You marked <span className="font-bold text-white">{lowIntensitySets.length} set{lowIntensitySets.length === 1 ? "" : "s"}</span> with 3 or more reps in reserve. Outside the 1–2 RIR target band — the set may not have been hard enough to drive growth.
                            </p>
                            <ul className="mt-2 space-y-1 text-[12px] text-white/75">
                                {lowIntensitySets.slice(0, 6).map((s, i) => (
                                    <li key={i} className="flex items-center gap-2" data-testid={`rir-low-item-${i}`}>
                                        <span className="h-1 w-1 flex-none rounded-full bg-amber-400" />
                                        <span className="truncate">{s.exercise} · Set {s.setNumber}{s.side ? ` (${s.side})` : ""} · RIR {s.rir}</span>
                                    </li>
                                ))}
                                {lowIntensitySets.length > 6 && (
                                    <li className="text-white/55">+ {lowIntensitySets.length - 6} more</li>
                                )}
                            </ul>
                            <p className="mt-3 text-[11px] uppercase tracking-wider text-white/55">
                                Next time, push closer to 1–2 RIR. Confirm if the log is accurate.
                            </p>
                        </div>
                    )}

                    <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <button
                            onClick={onCancel}
                            data-testid="rir-confirm-cancel"
                            className="rounded-sm border border-white/20 bg-white/5 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-white/10"
                        >
                            Cancel · let me fix it
                        </button>
                        <button
                            onClick={onConfirm}
                            data-testid="rir-confirm-go"
                            className="rounded-sm bg-accent-red px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-[color:var(--accent-hover)]"
                        >
                            Confirm · log the session
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
